// src/hooks/usePaymentProcessor.js
import { useState, useEffect, useCallback } from "react";
import { orderService } from "../services/OrderService";
import { multiPrinterService } from "../services/printer/printer.service";
import { printerConfigService } from "../services/printer/printerConfigService";
import { toast } from "react-hot-toast";

/**
 * Hook para procesar tickets de solicitud de pago para órdenes con estado "PagoSolicitado"
 * @param {number} pollingInterval - Intervalo de tiempo en milisegundos para consultar órdenes
 * @returns {Object} - Estado y funciones del procesador de pagos
 */
export const usePaymentProcessor = (pollingInterval = 10000) => {
  const [pendingPaymentOrders, setPendingPaymentOrders] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastProcessed, setLastProcessed] = useState(null);
  const [error, setError] = useState(null);

  // Función para obtener órdenes con solicitud de pago pendiente
  const fetchPendingPaymentOrders = useCallback(async () => {
    try {
      setError(null);
      const response = await orderService.getOrdersByStatus("PagoSolicitado");
      setPendingPaymentOrders(response.data || []);
      return response.data || [];
    } catch (err) {
      console.error("Error al obtener órdenes pendientes de pago:", err);
      setError(err.message || "Error al obtener órdenes pendientes de pago");
      return [];
    }
  }, []);

  // Función para imprimir ticket de solicitud de pago
  const printPaymentRequest = useCallback(async (order) => {
    console.log("ORDER PAYMENT: ", order);
    try {
      // Verificar si hay impresoras disponibles configuradas para tickets de cliente
      const printers = printerConfigService.getPrintersByDestination("Cliente");

      if (!printers || printers.length === 0) {
        console.warn("No hay impresoras configuradas para tickets de cliente");
        return false;
      }

      // Obtener la impresora por defecto para tickets de cliente, o usar la primera disponible
      const defaultPrinter =
        printerConfigService.getDefaultPrinterForDestination("Cliente") ||
        printers[0];

      if (!defaultPrinter) {
        throw new Error(
          "No se encontró una impresora disponible para tickets de cliente"
        );
      }

      // Formatear la orden para la impresora
      const printOrder = {
        _id: order._id || order.id,
        id: order._id || order.id,
        mesaId: order.mesaId || order.table,
        descriptionTable: order.table || order.descriptionTable,
        date: order.date,
        payment: order.payment,
        items: (order.products || order.items).map((item) => ({
          name: item.name,
          price: item.price || item.total / (item.quantity || 1),
          quantity: item.quantity || 1,
          selectedAddon: item.selectedAddon,
        })),
      };

      // Imprimir el ticket de solicitud de pago
      await multiPrinterService.printCustomerBill(
        printOrder,
        defaultPrinter.id
      );

      // Actualizar el estado de la orden a "En proceso de pago"
      await orderService.updateOrderStatus(
        order._id || order.id,
        "ProcesandoPago"
      );

      setLastProcessed({
        orderId: order._id || order.id,
        timestamp: new Date(),
        status: "success",
      });

      return true;
    } catch (err) {
      console.error("Error al imprimir ticket de solicitud de pago:", err);
      setLastProcessed({
        orderId: order._id || order.id,
        timestamp: new Date(),
        status: "error",
        error: err.message,
      });
      setError(err.message || "Error al imprimir ticket de solicitud de pago");
      return false;
    }
  }, []);

  // Función principal para procesar todas las órdenes pendientes de pago
  const processPaymentRequests = useCallback(async () => {
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      const orders = await fetchPendingPaymentOrders();

      if (orders.length === 0) {
        setIsProcessing(false);
        return;
      }

      console.log(`Procesando ${orders.length} órdenes pendientes de pago`);

      // Procesar cada orden secuencialmente
      for (const order of orders) {
        const success = await printPaymentRequest(order);
        if (success) {
          toast.success(
            `Solicitud de pago para mesa ${
              order.mesaId || order.table
            } procesada`
          );
        } else {
          toast.error(
            `Error al procesar solicitud de pago para mesa ${
              order.mesaId || order.table
            }`
          );
        }
      }
    } catch (err) {
      console.error("Error al procesar solicitudes de pago:", err);
      setError(err.message || "Error al procesar solicitudes de pago");
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, fetchPendingPaymentOrders, printPaymentRequest]);

  // Procesar manualmente una orden específica
  const processOrder = useCallback(
    async (orderId) => {
      setIsProcessing(true);
      try {
        const response = await orderService.getOrderById(orderId);
        const order = response.data;

        if (!order) {
          throw new Error(`Orden ${orderId} no encontrada`);
        }

        const success = await printPaymentRequest(order);
        if (success) {
          toast.success(
            `Solicitud de pago para mesa ${
              order.mesaId || order.table
            } procesada`
          );
          return true;
        } else {
          toast.error(
            `Error al procesar solicitud de pago para mesa ${
              order.mesaId || order.table
            }`
          );
          return false;
        }
      } catch (err) {
        console.error(`Error al procesar orden ${orderId}:`, err);
        setError(err.message || `Error al procesar orden ${orderId}`);
        return false;
      } finally {
        setIsProcessing(false);
      }
    },
    [printPaymentRequest]
  );

  // Efecto para iniciar el polling de órdenes pendientes de pago
  useEffect(() => {
    // Procesar inmediatamente al montar el componente
    processPaymentRequests();

    // Configurar intervalo de polling
    const interval = setInterval(() => {
      processPaymentRequests();
    }, pollingInterval);

    // Limpiar intervalo al desmontar
    return () => clearInterval(interval);
  }, [processPaymentRequests, pollingInterval]);

  return {
    pendingPaymentOrders,
    isProcessing,
    lastProcessed,
    error,
    processPaymentRequests,
    processOrder,
    fetchPendingPaymentOrders,
  };
};
